import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { documentApi } from "@api/documents";
import {
  Segment,
  Header,
  Icon,
  Dimmer,
  Loader,
  Image,
  Card,
  Label,
  Popup,
} from "semantic-ui-react";
import { useHistory } from "react-router-dom";

const Carousel = ({ isMobile = false }) => {
  const [recentBooks, setRecentBooks] = useState([]);
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const history = useHistory();

  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);
  const animationRef = useRef(null);
  const isPaused = useRef(false);

  // --- Fetch Books ---
  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    if (!isMountedRef.current) return;
    try {
      setLoading(true);
      setError(null);

      const recentQuery = documentApi
        .query()
        .withDocumentType("BOOK")
        .sortBy("-created")
        .withSize(8)
        .qs();
      const featuredQuery = documentApi
        .query()
        .withDocumentType("BOOK")
        .sortBy("-mostloaned")
        .withSize(8)
        .qs();

      const [recentResponse, featuredResponse] = await Promise.all([
        documentApi.list(recentQuery),
        documentApi.list(featuredQuery),
      ]);

      if (isMountedRef.current) {
        const recentData =
          recentResponse.data?.hits || recentResponse.data?.data || [];
        const featuredData =
          featuredResponse.data?.hits || featuredResponse.data?.data || [];
        setRecentBooks(recentData);
        setFeaturedBooks(featuredData);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError("Failed to load books data");
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  // --- Utility Functions ---
  const formatImageUrl = (book) => {
    if (book?.metadata?.cover_metadata?.urls) {
      const urls = book.metadata.cover_metadata.urls;
      return urls.large || urls.medium || urls.small;
    }
    if (book?.metadata?.cover_metadata?.length > 0) {
      const coverData = book.metadata.cover_metadata[0];
      return coverData.url || coverData.urls?.large || coverData.urls?.medium;
    }
    if (book?.links?.cover) return book.links.cover;
    return "/static/images/placeholder.png";
  };

  const getBookTitle = (book) =>
    book?.metadata?.title || book?.title || "Untitled";
  const getBookAuthors = (book) => {
    const authors = book?.metadata?.authors || book?.authors || [];
    if (!authors.length) return "Unknown Author";
    return authors.map((a) => a.full_name || a.name || a).join(", ");
  };

  const getCallNumber = (book) => book?.metadata?.call_number || "N/A";
  // const getAccessionNumber = (book) =>
  //   book?.metadata?.accession_number || 'N/A';

  // --- Prepare books ---
  const mixedBooks = [
    ...recentBooks.map((b) => ({ ...b, bookType: "recent" })),
    ...featuredBooks.map((b) => ({ ...b, bookType: "popular" })),
  ];

  const shuffleArray = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const duplicatedBooks = [
    ...shuffleArray(mixedBooks),
    ...shuffleArray(mixedBooks),
  ];

  // --- Drag & Touch Handlers ---
  const startDrag = (pageX) => {
    isDragging.current = true;
    startX.current = pageX - containerRef.current.offsetLeft;
    scrollStart.current = containerRef.current.scrollLeft;
    isPaused.current = true; // pause autoplay during drag
  };
  const moveDrag = (pageX) => {
    if (!isDragging.current) return;
    const x = pageX - containerRef.current.offsetLeft;
    const walk = x - startX.current;
    containerRef.current.scrollLeft = scrollStart.current - walk * 0.7;
  };
  const endDrag = () => {
    isDragging.current = false;
    isPaused.current = false;
  };

  const onMouseDown = (e) => startDrag(e.pageX);
  const onMouseMove = (e) => moveDrag(e.pageX);
  const onMouseUp = endDrag;
  const onMouseLeave = () => isDragging.current && endDrag();

  const onTouchStart = (e) => startDrag(e.touches[0].pageX);
  const onTouchMove = (e) => moveDrag(e.touches[0].pageX);
  const onTouchEnd = endDrag;

  // --- Pause/Resume Handlers (whole carousel) ---
  const pauseCarousel = () => {
    isPaused.current = true;
  };

  const resumeCarousel = () => {
    isPaused.current = false;
  };

  // --- Autoplay & Infinite Loop ---
  const animate = () => {
    if (!containerRef.current || isPaused.current) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    containerRef.current.scrollLeft += 0.8;

    if (containerRef.current.scrollLeft >= contentRef.current.scrollWidth / 2) {
      containerRef.current.scrollLeft = 0;
    }

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [duplicatedBooks.length]);

  // --- Book Card with Popup ---
  const BookCard = ({ book }) => (
    <Popup
      trigger={
        <Card
          style={{
            minWidth: "140px",
            maxWidth: "140px",
            height: "220px",
            margin: "0 8px",
            borderRadius: "10px",
            boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
            cursor: "pointer",
            flexShrink: 0,
            position: "relative",
            background: "#fff",
            overflow: "hidden",
            transition: "transform 0.3s ease",
            zIndex: 0,
          }}
          onClick={() =>
            book.metadata?.pid &&
            history.push(`/literature/${book.metadata.pid}`)
          }
        >
          <Label
            color={book.bookType === "recent" ? "blue" : "orange"}
            ribbon
            style={{
              position: "absolute",
              top: "8px",
              left: "0px",
              fontSize: "0.7em",
              fontWeight: 600,
              zIndex: 1,
            }}
          >
            <Icon
              name={
                book.bookType === "recent" ? "clock outline" : "star outline"
              }
              size="small"
            />
            {book.bookType === "recent" ? "Recent" : "Popular"}
          </Label>
          <div
            style={{
              height: "130px",
              overflow: "hidden",
              borderRadius: "10px 10px 0 0",
              background: "linear-gradient(145deg, #f0f0f0, #ffffff)",
              position: "relative",
              zIndex: 0,
            }}
          >
            <Image
              src={formatImageUrl(book)}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
          <Card.Content style={{ padding: "10px", height: "90px" }}>
            <Card.Header
              style={{
                fontSize: "0.8em",
                fontWeight: 600,
                lineHeight: "1.2",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                marginBottom: "6px",
              }}
            >
              {getBookTitle(book)}
            </Card.Header>
            <Card.Meta
              style={{
                fontSize: "0.7em",
                color: "#666",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                marginBottom: "6px",
              }}
            >
              {getBookAuthors(book)}
            </Card.Meta>
          </Card.Content>
        </Card>
      }
      content={
        <div
          style={{
            padding: "12px",
            borderRadius: "10px",
            background: "#ffffff",
            boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
            minWidth: "220px",
            maxWidth: "260px",
          }}
        >
          <Header
            as="h4"
            style={{
              fontSize: "1em",
              fontWeight: 700,
              marginBottom: "6px",
              color: "#2c3e50",
            }}
          >
            {getBookTitle(book)}
          </Header>
          <p
            style={{
              fontSize: "0.85em",
              marginBottom: "8px",
              color: "#555",
            }}
          >
            <Icon name="user" /> {getBookAuthors(book)}
          </p>
          <p style={{ fontSize: "0.8em", marginBottom: "4px" }}>
            <strong>Call No.:</strong> {getCallNumber(book)}
          </p>
          {/* You can uncomment later if needed */}
          {/* <p style={{ fontSize: '0.8em' }}>
            <strong>Accession No.:</strong> {getAccessionNumber(book)}
          </p> */}
        </div>
      }
      position="top center"
      hoverable
      style={{
        borderRadius: "10px",
        padding: "0",
      }}
    />
  );

  BookCard.propTypes = { book: PropTypes.object.isRequired };

  const segmentStyle = isMobile
    ? {
        background: "transparent",
        borderRadius: "16px",
        padding: "20px 10px",
        overflow: "hidden",
        width: "100%",
        marginLeft: "0",
      }
    : {
        background: "transparent",
        borderRadius: "16px",
        padding: "20px",
        overflow: "hidden",
        width: "150%",
        marginLeft: "-114px",
      };

  // --- Render ---
  if (loading)
    return (
      <Segment style={{ minHeight: "300px" }}>
        <Dimmer active>
          <Loader>Loading Books...</Loader>
        </Dimmer>
      </Segment>
    );
  if (error)
    return (
      <Segment
        textAlign="center"
        style={{ minHeight: "200px", padding: "40px" }}
      >
        <Icon name="exclamation triangle" color="red" size="big" />
        <Header as="h4" color="red">
          {error}
        </Header>
      </Segment>
    );

  return (
    <Segment style={segmentStyle}>
      <Segment
        style={{
          background: "transparent",
          borderRadius: "16px",
          padding: "20px",
          overflow: "hidden",
        }}
        onMouseEnter={pauseCarousel}
        onMouseLeave={resumeCarousel}
      >
        <div
          ref={containerRef}
          style={{
            display: "flex",
            overflowX: "hidden",
            cursor: "grab",
            userSelect: "none",
            padding: "10px 0",
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div ref={contentRef} style={{ display: "flex" }}>
            {duplicatedBooks.map((book, i) => (
              <BookCard key={i} book={book} />
            ))}
          </div>
        </div>
      </Segment>
    </Segment>
  );
};

Carousel.propTypes = {
  isMobile: PropTypes.bool,
};

export default Carousel;
